import { latestPropertyZestimateValue } from '@attik/types/property.types';
import { AddressForScheduler } from '@/util/types/globalTypes';
import {
  Modifier,
  RequiredInfoValue,
  ServWithMods,
  EquipmentQuantity,
  ConditionsMasterType,
  ConditionType,
} from '@/util/types/serverTypes';
import dayjs from 'dayjs';

// interface Bracket {
//   max: number | null;
//   min: number | null;
//   value: number;
// }

// interface ModifierData {
//   after?: number;
//   increment?: number;
//   scale?: number;
//   brackets?: Bracket[];
//   relation?: string;
//   limit?: number;
//   value?: number;
//   options?: Array<{ option: string; value: number }>;
//   customValue?: string;
//   basis?: keyof typeof basisSwitch;
//   addType: string;
//   calcType?: 'per' | string;
// }

// interface Modifier {
//   modType: keyof typeof modifierFn;
//   modiferData: ModifierData;
// }

// interface Service {
//   modifiers: Modifier[];
//   startingFee: number;
// }

interface ModifierResult {
  accum: number;
  desc: string;
  equipmentCategoryId?: string;
}

interface ModifierContextData {
  property: AddressForScheduler;
  requiredInfoValues: RequiredInfoValue[];
  equipmentQuantities: EquipmentQuantity[];
  serviceIds?: string[];
  inspectorIds?: string[];
  /** IDs of the service areas whose polygon contains the property (may be several). */
  serviceAreaIds?: string[];
}

// Helper function to evaluate a single condition
const evaluateCondition = (
  condition: ConditionType,
  contextData: ModifierContextData
): boolean => {
  const { attribute, condition: conditionType, value } = condition;

  // Get the actual value for the attribute
  const actualValue = getAttributeValue(attribute, contextData);

  if (actualValue === null || actualValue === undefined) {
    return false;
  }

  // Evaluate based on condition type
  switch (conditionType) {
    case 'is':
      // Array-valued attributes (e.g. inspector-id, service-area-id) match on membership
      if (Array.isArray(actualValue)) {
        return actualValue.some((v) => String(v) === String(value));
      }
      return actualValue.toString() === value;
    case 'is_not':
      if (Array.isArray(actualValue)) {
        return !actualValue.some((v) => String(v) === String(value));
      }
      return actualValue.toString() !== value;
    case 'contains':
      return actualValue.toString().includes(value);
    case 'not_contains':
      return !actualValue.toString().includes(value);
    case 'exists':
      return (
        actualValue !== null && actualValue !== undefined && actualValue !== ''
      );
    case 'not_exists':
      return (
        actualValue === null || actualValue === undefined || actualValue === ''
      );
    case 'greater_than':
      return Number(actualValue) > Number(value);
    case 'less_than':
      return Number(actualValue) < Number(value);
    case 'is_true':
      return actualValue === true || actualValue === 'true';
    case 'is_false':
      return actualValue === false || actualValue === 'false';
    // Add date conditions later if needed
    default:
      return false;
  }
};

// Helper function to get the actual value for an attribute
const getAttributeValue = (
  attribute: string,
  contextData: ModifierContextData
): any => {
  const {
    property,
    requiredInfoValues,
    equipmentQuantities,
    serviceIds = [],
    inspectorIds = [],
    serviceAreaIds = [],
  } = contextData;

  // Property-related attributes (only the modifier-specific ones)
  if (attribute === 'modifier-sqft') return property.sqft;
  if (attribute === 'modifier-age')
    return property.yearBuilt ? dayjs().year() - property.yearBuilt : null;
  if (attribute === 'modifier-unit-count') return property.unitCount;
  if (attribute === 'modifier-foundation') return property.foundation;
  if (attribute === 'modifier-zestimate') {
    return latestPropertyZestimateValue(property.zestimates);
  }

  // Inspector-related attributes
  if (attribute === 'inspector-id') return inspectorIds;

  // Service area attributes (which containing polygon(s) the property falls in)
  if (attribute === 'service-area-id') return serviceAreaIds;

  // Service-related attributes (from charges)
  if (attribute === 'service-ids') return serviceIds;

  // Required info attributes (format: required-info-{id})
  if (attribute.startsWith('required-info-')) {
    const requiredInfoId = attribute.replace('required-info-', '');
    const requiredInfo = requiredInfoValues.find(
      (info) => info._requiredInfoId === requiredInfoId
    );
    return requiredInfo?.value;
  }

  // Equipment attributes (format: equipment-{categoryId})
  if (attribute.startsWith('equipment-')) {
    const categoryId = attribute.replace('equipment-', '');
    const equipment = equipmentQuantities.find(
      (eq) => eq._equipmentCategoryId === categoryId
    );
    return equipment?.value;
  }

  return null;
};

// Function to evaluate all conditions
const evaluateConditions = (
  conditions: ConditionsMasterType,
  contextData: ModifierContextData
): boolean => {
  if (!conditions.groups.length) return false;

  const groupResults = conditions.groups.map((group) => {
    const conditionResults = group.conditions.map((condition) =>
      evaluateCondition(condition, contextData)
    );

    // Apply group operator (AND/OR within the group)
    return group.operator === 'AND'
      ? conditionResults.every((result) => result)
      : conditionResults.some((result) => result);
  });

  // Apply master operator (AND/OR between groups)
  return conditions.operator === 'AND'
    ? groupResults.every((result) => result)
    : groupResults.some((result) => result);
};

// interface ErrorResult {
//   addType: 'error';
//   accum: number;
//   desc: string;
//   basis: string;
// }

const modifierFn = {
  increment: (
    modData: Modifier['modiferData'],
    value: number
  ): ModifierResult => {
    const { after = 0, increment = 0, scale = 1 } = modData;
    const incriment = Math.ceil(Math.max(0, value - after) / scale) * increment;
    return {
      accum: incriment,
      desc: `Increment: Added ${incriment} to ${modData.addType} based on ${
        modData.requiredInfoField?.name || modData.basis
      }`,
    };
  },

  bracket: (
    modData: Modifier['modiferData'],
    value: number,
    base: number
  ): ModifierResult => {
    const { brackets = [] } = modData;
    const bracket = brackets.find((bracket) => {
      const max = bracket.max === null ? Infinity : bracket.max;
      const min = bracket.min === null ? -Infinity : bracket.min;
      if (min === 0) return min <= value && max >= value;
      return min < value && max >= value;
    });

    if (!bracket) {
      return { accum: 0, desc: 'No matching bracket found' };
    }

    if (modData.calcType === 'per') {
      const finalAdd = bracket.value * value - base;
      return {
        accum: finalAdd,
        desc: `Bracket: Added ${finalAdd} to ${modData.addType} based on ${
          modData.requiredInfoField?.name || modData.basis
        }`,
      };
    }
    return {
      accum: bracket.value,
      desc: `Bracket: Added ${bracket.value} to ${modData.addType} based on ${
        modData.requiredInfoField?.name || modData.basis
      }`,
    };
  },

  compare: (
    modData: Modifier['modiferData'],
    value: number | string
  ): ModifierResult => {
    const {
      relation = '==',
      limit = 0,
      value: increase = 0,
      basisType,
      compareValue = '',
    } = modData;
    // console.log('compare', modData, value);
    const operators: Record<
      string,
      (a: number | string, b: number | string) => boolean
    > = {
      '==': (a, b) => a === b,
      '!=': (a, b) => a !== b,
      '<=': (a, b) => a <= b,
      '>=': (a, b) => a >= b,
      '<': (a, b) => a < b,
      '>': (a, b) => a > b,
    };

    if (basisType === 'number') {
      if (operators[relation](value, limit)) {
        return {
          accum: increase,
          desc: `Compare: Added ${increase} to ${modData.addType} based on ${
            modData.requiredInfoField?.name || modData.basis
          } (${modData.relation} ${modData.limit})`,
        };
      }
    } else {
      if (operators[relation](value.toString(), compareValue)) {
        return {
          accum: increase,
          desc: `Compare: Added ${increase} to ${modData.addType} based on ${
            modData.requiredInfoField?.name || modData.basis
          } (${modData.relation} ${modData.compareValue})`,
        };
      }
    }
    return {
      accum: 0,
      desc: `Compare: ${
        modData.requiredInfoField?.name || modData.basis
      } conditions not met. (${modData.relation} ${
        modData.limit || modData.compareValue
      })`,
    };
  },

  enum: (modData: Modifier['modiferData'], value: number): ModifierResult => {
    const { options = [] } = modData;
    const option = options.find((option) => option.option === value.toString());
    return {
      accum: option?.value ?? 0,
      desc: `Enum: ${modData.requiredInfoField?.name || modData.basis}`,
    };
  },

  zipcodes: (
    modData: Modifier['modiferData'],
    value: number // The value could be string or number, we'll convert to string
  ): ModifierResult => {
    const { zipcodes = [], value: increase = 0 } = modData;
    const zipcode = value?.toString();

    // Check if the property's zipcode is in our list
    if (zipcode && zipcodes.includes(zipcode)) {
      return {
        accum: increase,
        desc: `Zipcode: Added ${increase} to ${modData.addType} for zipcode ${zipcode}`,
      };
    }
    return {
      accum: 0,
      desc: `Zipcode: ${zipcode || 'unknown'} not in modifier list`,
    };
  },

  requested: (
    modData: Modifier['modiferData'],
    value: boolean
  ): ModifierResult => {
    if (value) {
      return {
        accum: modData.value ?? 0,
        desc: `Requested: Added ${modData.value ?? 0} to ${modData.addType}`,
      };
    }
    return { accum: 0, desc: `Not Requested` };
  },

  conditional: (
    modData: Modifier['modiferData'],
    contextData: ModifierContextData
  ): ModifierResult => {
    const { conditions, value = 0 } = modData;

    if (!conditions || !conditions.groups.length) {
      return { accum: 0, desc: 'Conditional: No conditions defined' };
    }

    const isConditionMet = evaluateConditions(conditions, contextData);

    if (isConditionMet) {
      return {
        accum: value,
        desc: `Conditional: Added ${value} to ${modData.addType} (conditions met)`,
      };
    }

    return { accum: 0, desc: 'Conditional: Conditions not met' };
  },
};

const basisSwitch = {
  sqft: (property: AddressForScheduler) => property.sqft,
  age: (property: AddressForScheduler) =>
    !!property.yearBuilt ? dayjs().year() - property.yearBuilt : null,
  'unit count': (property: AddressForScheduler) => property.unitCount,
  zipcode: (property: AddressForScheduler) => +property.zip,
  foundation: (property: AddressForScheduler) => property.foundation,
  requested: (property: AddressForScheduler, requested: boolean) => requested,
};

export const modifierPriceAdjFn = (
  service: ServWithMods & { startingFee: number; isPrimary: boolean },
  property: AddressForScheduler,
  requested = false,
  requiredInfoValues: RequiredInfoValue[] = [],
  equipmentQuantities: EquipmentQuantity[] = [],
  serviceIds: string[] = [],
  inspectorIds: string[] = [],
  serviceAreaIds: string[] = []
):
  | {
      finalPrice: number;
      finalDuration: number;
      finalAdmin: number;
      equipmentAdjustments: EquipmentQuantity[];
      explanation: string[];
    }
  | { error: string } => {
  // console.log('modifierPriceAdjFn', service, property, requested);

  const calcedMods = service.modifiers.map((modifier) => {
    // Handle conditional modifiers differently
    if (modifier.modType === 'conditional') {
      const contextData: ModifierContextData = {
        property,
        requiredInfoValues,
        equipmentQuantities,
        serviceIds,
        inspectorIds,
        serviceAreaIds,
      };

      const result = {
        addType: modifier.modiferData.addType,
        ...modifierFn.conditional(modifier.modiferData, contextData),
      };

      // Add equipmentCategoryId for equipment modifiers
      if (
        modifier.modiferData.addType === 'equipment' &&
        modifier.modiferData.equipmentCategoryId
      ) {
        result.equipmentCategoryId = modifier.modiferData.equipmentCategoryId;
      }

      return result;
    }

    // Handle traditional modifiers (existing logic)
    let value: number | boolean | string;
    let requiredInfoValue = requiredInfoValues.find(
      (info) => info._requiredInfoId === modifier.modiferData?.basis
    );
    let equipmentQuantity = equipmentQuantities.find(
      (eq) => eq._equipmentCategoryId === modifier.modiferData?.basis
    );

    // console.log({ modifier, equipmentQuantity });
    // console.log(
    //   'requiredInfoValue',
    //   requiredInfoValue,
    //   modifier.modiferData,
    //   requiredInfoValues,
    //   serviceIds,
    //   inspectorIds
    // );
    // Check if there is a custom value for the basis
    if (modifier.modiferData?.customValue)
      value = +modifier.modiferData.customValue;
    // If there is no custom value, check to see if it is a value pulled from the property info
    else if (requiredInfoValue)
      value =
        modifier.modiferData.basisType === 'number'
          ? +requiredInfoValue.value
          : requiredInfoValue.value;
    else if (equipmentQuantity) value = equipmentQuantity.value;
    else if (
      modifier.modiferData?.basis &&
      modifier.modiferData.basis in basisSwitch
    ) {
      let basisValue = basisSwitch[
        modifier.modiferData.basis as keyof typeof basisSwitch
      ](property, requested);

      // If an actual basis value is found, then add that as the value for the modifier.
      if (basisValue !== null && basisValue !== undefined) value = basisValue;
      // If there is no basis value found, return and error.
      else
        return {
          addType: 'error',
          accum: 0,
          desc: 'No value for basis within property',
          basis: modifier.modiferData?.basis,
        };
    } else {
      // Check if this is a missing requiredInfoValue (field was deleted)
      // If the basis looks like a requiredInfo ID but wasn't found, skip this modifier
      if (
        modifier.modiferData?.basis &&
        modifier.modiferData.basis.length === 24 && // MongoDB ObjectId length
        !requiredInfoValue
      ) {
        return {
          addType: 'skip',
          accum: 0,
          desc: `Skipping modifier due to missing required info field: ${modifier.modiferData.basis}`,
          basis: modifier.modiferData?.basis,
        };
      }

      return {
        addType: 'error',
        accum: 0,
        desc: 'No value for basis',
        basis: modifier.modiferData?.basis,
      };
    }

    const result = {
      addType: modifier.modiferData.addType,
      ...modifierFn[modifier.modType](
        modifier.modiferData,
        // @ts-ignore
        value,
        service.startingFee
      ),
    };

    // Add equipmentCategoryId for equipment modifiers
    if (
      modifier.modiferData.addType === 'equipment' &&
      modifier.modiferData.equipmentCategoryId
    ) {
      result.equipmentCategoryId = modifier.modiferData.equipmentCategoryId;
    }

    return result;
  });

  // Filter out skipped modifiers (missing required info fields)
  const validMods = calcedMods.filter((res) => res.addType !== 'skip');

  if (validMods.some((res) => res.addType === 'error'))
    return {
      error: `Missing Data: ${[
        ...new Set(
          validMods
            .filter((res) => res.addType === 'error')
            .map((x) => (x as any).basis)
        ),
      ]}`,
    };

  const totaltime =
    validMods
      .filter((x) => x.addType === 'time')
      .reduce((acc, mod) => acc + mod.accum, 0) / 60;

  const totalcost = validMods
    .filter((x) => x.addType === 'cost')
    .reduce((acc, mod) => acc + mod.accum, 0);

  const totalAdmin = validMods
    .filter((x) => x.addType === 'admin')
    .reduce((acc, mod) => acc + mod.accum, 0);

  // Calculate equipment adjustments
  const equipmentAdjustments: EquipmentQuantity[] = validMods
    .filter(
      (x) =>
        x.addType === 'equipment' &&
        'equipmentCategoryId' in x &&
        x.equipmentCategoryId
    )
    .reduce((acc, mod) => {
      const categoryId = (mod as any).equipmentCategoryId;
      const existing = acc.find((eq) => eq._equipmentCategoryId === categoryId);

      if (existing) {
        existing.value += mod.accum;
      } else {
        acc.push({
          _equipmentCategoryId: categoryId,
          value: mod.accum,
        });
      }
      return acc;
    }, [] as EquipmentQuantity[]);

  // console.log({ calcedMods, totaltime, totalcost, equipmentAdjustments });

  return {
    finalPrice: totalcost,
    finalDuration: totaltime,
    finalAdmin: totalAdmin,
    equipmentAdjustments,
    explanation: validMods.filter((x) => x.accum !== 0).map((x) => x.desc),
  };
};
